import { ClientTaskTypeValue } from '../enum/client-task-type';
import { TaskStatus } from '../enum/task-status';

export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/** Assigned + Submitted — used by Search By = Open. */
export const OPEN_TASK_STATUSES = [TaskStatus.NotCompleted, TaskStatus.Completed, TaskStatus.Reassigned];

/** Query filters for GET /api/app/task */
export type TaskSearchByValue = 'all' | 'open';

export interface EmployerTaskListFilter {
  matterId?: string | null;
  companyId?: string | null;
  companyName?: string | null;
  searchBy?: TaskSearchByValue | null;
  statuses?: number[] | null;
  status?: number | null;
}

/** TasksEntityType — matter-created and company-created tasks both use Company. */
export const TasksEntityType = {
  InternalGipUser: 0,
  ForeignNational: 1,
  Company: 2,
} as const;

export type TasksEntityTypeValue = (typeof TasksEntityType)[keyof typeof TasksEntityType];

export interface EmployerTaskFile {
  id?: string;
  fileName?: string | null;
  sharePointFilePath?: string | null;
  mimeType?: string | null;
  documentTitle?: string | null;
  documentType?: string | null;
  createdBy?: string | null;
  creationTime?: string;
  attachedByGCM?: boolean;
  isSubmitted?: boolean;
  status?: number | null;
  decision?: number | null;
}

export interface EmployerTaskType {
  id?: string;
  taskType: ClientTaskTypeValue;
  instructions?: string | null;
  confirmationStatement?: string | null;
  documentTitle?: string | null;
  documentType?: string | null;
  questionnaireId?: string | null;
  questionnaireName?: string | null;
  data?: string | null;
  files?: EmployerTaskFile[];
}

/** GET /api/app/task/details-by-action-id response. */
export interface TaskDetailsByAction {
  taskId: string;
  status: number;
}

/** Runtime employer task from GET/POST /api/app/task. */
export interface EmployerTaskHistory {
  id?: string;
  taskId?: string;
  historyType?: number;
  newStatus?: number;
  previewStatus?: number;
  performedByUserId?: string | null;
  performedByEmployerId?: string | null;
  performedOn?: string;
  comments?: string | null;
}

export interface EmployerTaskUserDetails {
  userId?: string | null;
  userName?: string | null;
}

export interface EmployerTask {
  id: string;
  taskId?: string;
  businessCode?: string | null;
  matterId?: string | null;
  taskTemplateId: string;
  companyId?: string | null;
  companyName?: string | null;
  foreignNationalId?: string | null;
  title?: string | null;
  description?: string | null;
  status: number;
  comment?: string | null;
  reason?: string | null;
  currentTaskReviewId?: string | null;
  completedOn?: string | null;
  lastModified?: string | null;
  assignedOn?: string | null;
  userDetails?: EmployerTaskUserDetails | null;
  entityType?: TasksEntityTypeValue | null;
  entityId?: string | null;
  matterActionId?: string | null;
  types?: EmployerTaskType[];
  histories?: EmployerTaskHistory[];
}

/** POST /api/app/task request body (CreateUpdateTaskDto). */
export interface CreateEmployerTaskDto {
  matterId: string | null;
  taskTemplateId: string;
  companyId?: string | null;
  foreignNationalId?: string | null;
  title: string;
  description?: string | null;
  status?: number;
  currentTaskReviewId?: string | null;
  completedOn?: string | null;
  entityType?: TasksEntityTypeValue | null;
  entityId?: string | null;
  /** MatterAction.Id for Create Task workflow action (type 8). */
  matterActionId?: string | null;
  types: EmployerTaskType[];
}

export interface EmployerTaskAttachmentUpload {
  fileName: string;
  sharePointFilePath?: string;
  fileSize?: number;
  mimeType?: string;
}

/** Allowed statuses for POST /api/app/task/update-status (string enum). */
export type UpdateTaskStatusValue = 'Approved' | 'Rejected' | 'Reassigned' | 'Cancelled';

export interface UpdateTaskStatusDocumentWithType {
  id: string;
  destination: 'SupportDocs' | 'Matter' | string;
  documentType: string;
  userAction?: unknown | null;
}

/** POST /api/app/task/update-status request body. */
export interface UpdateTaskStatusDto {
  taskId: string;
  status: UpdateTaskStatusValue;
  comment?: string | null;
  reason?: string | null;
  documentsWithType?: UpdateTaskStatusDocumentWithType[] | null;
}
